import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  OfficeWithdrawalQuestion,
  OfficeWithdrawalRequestWithdrawalForm,
} from '@/context/services/office_withdrawal/type';
import { useTranslation } from '@/i18n';
import { Heading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { memo } from 'react';
import { Control, UseFormResetField } from 'react-hook-form';
import styles from '../../index.module.scss';
import { QuestionItem } from './QuestionItem';

const cx = classnames.bind(styles);

type Props = {
  control: Control<OfficeWithdrawalRequestWithdrawalForm>;
  resetField: UseFormResetField<OfficeWithdrawalRequestWithdrawalForm>;
  questions: OfficeWithdrawalQuestion[];
  isLoading?: boolean;
};

const CHECKBOX_NAMES = {
  1: 'question_1',
  2: 'question_2',
  3: 'question_3',
  4: 'question_4',
  5: 'question_5',
} as const satisfies Record<
  number,
  keyof OfficeWithdrawalRequestWithdrawalForm
>;

type QuestionIndex = keyof typeof CHECKBOX_NAMES;

const TEXT_NAMES: Partial<
  Record<QuestionIndex, keyof OfficeWithdrawalRequestWithdrawalForm>
> = {
  3: 'question_3_text',
  4: 'question_4_text',
  5: 'question_5_text',
};

const isValidQuestionIndex = (n: number): n is QuestionIndex =>
  n in CHECKBOX_NAMES;

export const ReasonSection = memo(
  ({ control, resetField, questions, isLoading }: Props) => {
    const { t } = useTranslation();

    return (
      <div className={cx(styles['section-content'])}>
        <Heading as='h4' className={cx(styles['heading'])}>
          {t('office_withdrawal.reason')}
        </Heading>
        <div className={cx(styles['description-text'])}>
          {t('office_withdrawal.form_description')}
        </div>
        {isLoading ? (
          <LoadingSkeletonList repeatCount={5} />
        ) : (
          <div>
            {questions.map((question, index) => {
              const questionIndex = index + 1;
              if (!isValidQuestionIndex(questionIndex)) return null;
              return (
                <div key={question.id}>
                  <QuestionItem
                    question={question}
                    control={control}
                    resetField={resetField}
                    checkboxName={CHECKBOX_NAMES[questionIndex]}
                    textName={TEXT_NAMES[questionIndex]}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

ReasonSection.displayName = 'ReasonSection';
